import React, { useRef, useCallback, useEffect, useState } from 'react';

interface CrossBrowserVideoProps {
  className?: string;
  style?: React.CSSProperties;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
  poster?: string;
  onLoadedData?: () => void;
  onError?: () => void;
  sources: {
    src: string;
    type: string;
    media?: string;
  }[];
  fallbackImage?: string;
}

const CrossBrowserVideo: React.FC<CrossBrowserVideoProps> = ({
  className,
  style,
  autoPlay = true,
  muted = true,
  loop = true,
  playsInline = true,
  preload = 'none',
  poster,
  onLoadedData,
  onError,
  sources,
  fallbackImage
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);
  const [currentSourceIndex, setCurrentSourceIndex] = useState(0);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Handle video load success
  const handleVideoLoad = useCallback(() => {
    setVideoError(false);
    onLoadedData?.();
    
    // Try to play the video
    if (videoRef.current && autoPlay) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Autoplay failed - this is normal in many browsers
        });
      }
    }
  }, [autoPlay, onLoadedData]);

  // Handle video error and try next source
  const handleVideoError = useCallback(() => {
    const nextIndex = currentSourceIndex + 1;
    if (nextIndex < sources.length && videoRef.current) {
      setCurrentSourceIndex(nextIndex);
      videoRef.current.src = sources[nextIndex].src;
      videoRef.current.load();
    } else {
      setVideoError(true);
      onError?.();
    }
  }, [currentSourceIndex, sources, onError]);

  // Handle user interaction to enable autoplay
  const handleUserInteraction = useCallback(() => {
    if (!hasUserInteracted && videoRef.current && autoPlay) {
      setHasUserInteracted(true);
      videoRef.current.play().catch(() => {
        // Still failed after user interaction
      });
    }
  }, [hasUserInteracted, autoPlay]);

  // Set up user interaction listeners
  useEffect(() => {
    const handleClick = () => handleUserInteraction();
    const handleKeyPress = () => handleUserInteraction();
    const handleTouchStart = () => handleUserInteraction();

    document.addEventListener('click', handleClick, { once: true });
    document.addEventListener('keypress', handleKeyPress, { once: true });
    document.addEventListener('touchstart', handleTouchStart, { once: true });

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keypress', handleKeyPress);
      document.removeEventListener('touchstart', handleTouchStart);
    };
  }, [handleUserInteraction]);

  // Detect browser capabilities
  const getBrowserVideoSupport = () => {
    const video = document.createElement('video');
    return {
      mp4: video.canPlayType('video/mp4'),
      webm: video.canPlayType('video/webm'),
      ogg: video.canPlayType('video/ogg')
    };
  };

  // Filter sources based on browser support
  const getSupportedSources = () => {
    const support = getBrowserVideoSupport();
    return sources.filter(source => {
      if (source.type.includes('mp4')) return support.mp4;
      if (source.type.includes('webm')) return support.webm;
      if (source.type.includes('ogg')) return support.ogg;
      return true; // Allow other formats to be tried
    });
  };

  const supportedSources = getSupportedSources();

  if (videoError && fallbackImage) {
    return (
      <img
        src={fallbackImage}
        alt="Video background"
        className={className}
        style={style}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      className={className}
      style={style}
      autoPlay={autoPlay}
      muted={muted}
      loop={loop}
      playsInline={playsInline}
      preload={preload}
      poster={poster}
      onLoadedData={handleVideoLoad}
      onError={handleVideoError}
      // iOS Safari specific attributes
      {...({ 'webkit-playsinline': 'true' } as any)}
      {...({ 'x-webkit-airplay': 'allow' } as any)}
    >
      {supportedSources.map((source, index) => (
        <source
          key={index}
          src={source.src}
          type={source.type}
          media={source.media}
        />
      ))}
      
      {/* Fallback for browsers that don't support HTML5 video */}
      <p>
        Your browser does not support the video tag. 
        {fallbackImage && (
          <img src={fallbackImage} alt="Video background" className={className} style={style} />
        )}
      </p>
    </video>
  );
};

export default CrossBrowserVideo;